// ================= MOVE ART =================
//
// A figure per combat move — the move as it looks when it is USED. This is a different slot in the
// visual language from the Style sigils, and deliberately the opposite rule: sigils are "structure,
// rhythm, or force, never a fighter" because they name an IDENTITY, and an identity drawn as a
// fighter would claim one body for a whole style. A move IS a body doing a thing, so move art is a
// figure caught doing it.
//
// ONE FIGURE PER ARCHETYPE, NOT PER CARD. 194 cards, 26 archetypes. The classifier below maps a card
// to its archetype from the data the pool already carries — traits first (measured: every card has
// at least one), the id for the handful of cases where the trait is coarser than the move ("mawashi"
// is a Kick, but it is a ROUND kick). Nothing here reads the engine and nothing here is read by it:
// art only.
//
// TWO RENDERERS FOR ONE POSE, AND THE RASTER IS NOT THE WHOLE PICTURE. js/move-art-figures.js maps
// (artKey, pose) to a 512px silhouette of THAT FIGHTER performing the move — so a Judo deck's throw
// is the Judo fighter's throw. Where a figure exists it draws the BODY; where it does not, the SVG
// pose below draws the body instead. Either way the MOTION — the arc, the contact ring — is drawn
// by the SVG on top, never baked into the raster, because the arc takes the role colour (attack red,
// defense teal) and a baked arc could not change. That split is why the glyphs are stored whole and
// separated at load time (`splitGlyph`): one drawing, two layers, no duplicated geometry.
//
// DRAWING RULES for the SVG poses, so the fallback set reads as one hand:
//   32x32 viewBox - the figure faces RIGHT (the action direction everywhere in the app, and the
//   direction every delivered raster faces, which is what lets the two be swapped)
//   body: stroke 2, round caps/joins, no fill - head: filled circle r~2.5
//   motion: stroke 1.4, dashed, in .ma-motion - never more than one idea of it
//   colour: currentColor throughout, tinted by the surface that places it
(function () {
  'use strict';

  const P = (d, cls) => `<path d="${d}" ${cls ? `class="${cls}" ` : ''}fill="none" stroke="currentColor"/>`;
  const HEAD = (x, y, r) => `<circle cx="${x}" cy="${y}" r="${r || 2.5}" fill="currentColor" stroke="none"/>`;
  const wrap = inner => '<svg viewBox="0 0 32 32" aria-hidden="true" class="ma-glyph">' + inner + '</svg>';

  const GLYPHS = {

    // ---- hands ---------------------------------------------------------------------------------
    punch_straight: wrap(
      HEAD(19.5, 7) +
      P('M11.5,19.5 L18,10') +                       // torso, driving forward
      P('M17.5,10.5 L29.5,8.5') +                    // the punch, full extension
      '<circle cx="29.5" cy="8.5" r="2.8" fill="none" stroke="currentColor" class="ma-contact"/>' +
      P('M17.5,10.5 L14,13.5') +                     // rear hand at the chin
      P('M11.5,19.5 L16.5,24 L17,29.5') +            // lunging front leg
      P('M11.5,19.5 L6,29.5') +                      // rear leg, straight drive
      P('M22.5,4.6 L27.5,4 M22.5,13.4 L27,12.9', 'ma-motion')),

    punch_hook: wrap(
      HEAD(16.5, 7) +
      P('M13,19 L16,9.5') +
      P('M15.5,10 L25.5,10.5 L21,5.5') +             // arm folded, fist whipping in
      P('M15.5,10 L12,13') +
      P('M13,19 L9.5,29.5') +
      P('M13,19 L17.5,24 L16.5,29.5') +
      P('M28,15.5 A 11 11 0 0 0 18.5,3.8', 'ma-motion')),

    punch_upper: wrap(
      HEAD(17.5, 6.5) +
      P('M12.5,19.5 L16.5,9.5') +
      P('M16,10.5 L18.5,16 L23,7.5') +               // elbow dropped, fist rising
      P('M16,10.5 L12.5,13.5') +
      P('M12.5,19.5 L8,29.5') +
      P('M12.5,19.5 L16.5,24.5 L16,29.5') +
      P('M27,16 A 10 10 0 0 0 21.5,5.5', 'ma-motion')),

    // ---- legs ----------------------------------------------------------------------------------
    kick_front: wrap(
      HEAD(13, 6.5) +
      P('M12,17.5 L13.5,9') +
      P('M12,17.5 L19,14.5 L28.5,12') +              // snapping through, ball of the foot
      P('M12,17.5 L11,29.5') +
      P('M13.5,9.5 L18,12.5 M13.5,9.5 L9.5,13') +
      P('M22,7.8 L27.5,6.6', 'ma-motion')),

    kick_round: wrap(
      HEAD(10.5, 9) +
      P('M12.5,19 L11,10.5') +                       // body tilted off the pivot
      P('M12.5,19 L21.5,16.5 L29.5,10.5') +          // the whip, rising to the head
      '<circle cx="29.5" cy="10.5" r="2.8" fill="none" stroke="currentColor" class="ma-contact"/>' +
      P('M12.5,19 L9,29.5') +
      P('M11.5,11 L16,13 M11.5,11 L7.5,14.5') +
      P('M13.5,3.5 A 15 15 0 0 1 29,9.5', 'ma-motion')),

    kick_side: wrap(
      HEAD(8.5, 8) +
      P('M13,17 L9.5,9.5') +                         // leaning away from the thrust
      P('M13,17 L29.5,15.5') +                       // straight through, hip behind it
      '<circle cx="29.5" cy="15.5" r="2.8" fill="none" stroke="currentColor" class="ma-contact"/>' +
      P('M13,17 L10.5,29.5') +
      P('M10,10.5 L14.5,13') +
      P('M23,10.8 L28.5,10.2 M23,20.4 L28,19.9', 'ma-motion')),

    kick_axe: wrap(
      HEAD(12, 7) +
      P('M12,18.5 L12.5,9.5') +
      P('M12,18.5 L18,10 L21.5,3.5') +               // leg carried all the way up
      P('M12,18.5 L10,29.5') +
      P('M12.5,10.5 L8,13.5') +
      P('M22.5,4.5 A 12 12 0 0 1 25.5,16.5', 'ma-motion')),  // the heel's way down

    sweep: wrap(
      HEAD(11, 12) +
      P('M10,21 L11,14.5') +                         // dropped low
      P('M10,21 L6.5,25.5 L8.5,29.5') +              // support leg folded deep
      P('M10,21 L28,26') +                           // sweeping leg long across the floor
      P('M11.5,15 L16,17.5') +
      P('M12.5,29.5 A 18 18 0 0 1 27.5,23.5', 'ma-motion')),

    knee: wrap(
      HEAD(15, 6) +
      P('M13.5,17.5 L15,8.5') +
      P('M13.5,17.5 L22,12.5 L19.5,17.5') +          // knee spiking up, shin folded
      P('M13.5,17.5 L11.5,29.5') +
      P('M15,9.5 L21.5,6.5 M15,11 L21.5,9') +        // both hands pulling the head down
      P('M25.5,17.5 A 9 9 0 0 0 21.5,8.5', 'ma-motion')),

    elbow: wrap(
      HEAD(16, 7) +
      P('M13,18.5 L15.5,9') +
      P('M15.5,10 L25,8.5 L18.5,5.5') +              // folded tight, the point leading
      P('M13,18.5 L9.5,29.5') +
      P('M13,18.5 L17.5,24 L17,29.5') +
      P('M26.5,15.5 A 10 10 0 0 0 20.5,4.5', 'ma-motion')),

    // ---- grappling -----------------------------------------------------------------------------
    throw: wrap(
      HEAD(14.5, 11.5) +
      P('M10,21.5 C11,15.5 12.5,13 14.5,12') +       // thrower folded forward, hip in
      P('M10,21.5 L8,29.5 M10,21.5 L15,25.5 L15.5,29.5') +
      '<circle cx="25.5" cy="8.5" r="2.2" fill="currentColor" stroke="none" class="ma-partner"/>' +
      P('M25.5,9 L28.5,13.5', 'ma-partner') +        // the other body, mid-flight
      P('M6.5,5 C13,1.5 20.5,2.5 24.5,7', 'ma-motion')),

    takedown: wrap(
      HEAD(23.5, 14, 2.4) +
      P('M10,22.5 L21.5,15.5') +                     // level changed, driving through
      P('M21.5,15.5 L29.5,19 M21,16.5 L28.5,22.5') + // both arms wrapping the legs
      P('M10,22.5 L4,29 M10,22.5 L13.5,27.5 L11,29.5') +
      P('M5,17.5 L10.5,15', 'ma-motion')),

    trip: wrap(
      HEAD(14, 7) +
      P('M13,18 L14,9') +
      P('M13,18 L20.5,22.5 C24,24.5 26.5,22.5 26,19.5') +  // the foot hooking behind
      P('M13,18 L10.5,29.5') +
      P('M14,10 L21.5,8 M14,11.5 L20.5,12.5') +      // hands stealing the balance
      P('M27.5,25.5 A 7 7 0 0 0 21,28', 'ma-motion')),

    clinch: wrap(
      HEAD(11, 6.5) +
      '<circle cx="21" cy="6.5" r="2.5" fill="currentColor" stroke="none" class="ma-partner"/>' +
      P('M11.5,18 L11,9') +                          // two bodies, upright, joined
      P('M20.5,18 L21,9', 'ma-partner') +
      P('M11,9.5 L21,12 M21,9.5 L11,12') +           // arms crossed inside
      P('M11.5,18 L9,29.5 M11.5,18 L14.5,29.5') +
      P('M20.5,18 L23,29.5 M20.5,18 L17.5,29.5', 'ma-partner')),

    // ---- position & defence --------------------------------------------------------------------
    guard: wrap(
      HEAD(16, 6.5) +
      P('M12.5,7 L12.5,15 M19.5,7 L19.5,15') +       // both forearms tight beside the head
      P('M12.5,15 L15,17.5 M19.5,15 L17,17.5') +     // elbows tucked to the body
      P('M16,19.5 L16,16.5') +
      P('M16,19.5 L13,29.5 M16,19.5 L19,29.5')),

    frame: wrap(
      HEAD(13, 6.5) +
      P('M12,18 L13,8.5') +
      P('M13,9.5 L27.5,15') +                        // the arm locked out as a strut
      P('M20.5,12.5 L18.5,16.5') +                   // braced under, a truss not a punch
      P('M12,18 L8,29.5 M12,18 L16,24.5 L16.5,29.5')),

    trap: wrap(
      P('M5.5,21.5 C12,17.5 18.5,13 26.5,8.5 L29,10.5') +  // two forearms finding each other
      P('M5.5,9 C12,12 19,16 26.5,20.5 L28.5,18') +
      '<circle cx="16" cy="14.8" r="1.6" fill="none" stroke="currentColor" class="ma-motion"/>'),

    counter: wrap(
      HEAD(15, 7) +
      P('M13.5,18.5 L15,9.5') +
      P('M17.5,4.5 L15.5,12.5') +                    // the forearm the attack dies on
      P('M15,11 L29,9.5') +                          // the answer, already on its way
      P('M13.5,18.5 L9,29.5 M13.5,18.5 L17,24.5 L17,29.5') +
      P('M3.5,9.5 L11.5,10.5', 'ma-motion')),        // what came in, dying at the forearm

    evade: wrap(
      HEAD(10.5, 10) +
      P('M14.5,19 C15,14.5 12.5,12.5 11,11') +       // spine curling off the line
      P('M11.5,11.5 L16,13.5 M11.5,11.5 L8,14.5') +
      P('M14.5,19 L11.5,29.5 M14.5,19 L18.5,25 L19.5,29.5') +
      P('M21.5,4.5 A 10 10 0 0 1 13,6.5', 'ma-motion')),  // where the head just was

    footwork: wrap(
      HEAD(13, 7) +
      P('M12.5,17.5 L13,9') +
      P('M12.5,17.5 L7,27.5') +
      P('M12.5,17.5 L19,24 L20.5,29.5') +            // caught mid-stride
      P('M13,10 L17,12.5 M13,10 L9.5,13') +
      P('M23.5,23.5 L26.5,26 L23.5,28.5 M27,23.5 L30,26 L27,28.5', 'ma-motion')),

    pressure: wrap(
      HEAD(17, 8) +
      P('M12,19.5 L16.5,9.5') +                      // the whole body leaning in
      P('M16,11 L22.5,9 M16,12.5 L21.5,14') +
      P('M12,19.5 L8,29.5 M12,19.5 L17.5,25 L19,29.5') +
      P('M24.5,11.5 L27.5,14 L24.5,16.5', 'ma-motion')),

    rotation: wrap(
      HEAD(16, 6.5) +
      P('M15,18.5 L16,8.5') +
      P('M15.5,10 L22.5,8 M15.5,11 L9,13.5') +       // arms flung opposite ways
      P('M15,18.5 L11.5,29.5 M15,18.5 L18.5,24.5 L17.5,29.5') +
      P('M24,15 A 8 8 0 1 1 15,7.2', 'ma-motion')),  // the torque, most of a circle

    read: wrap(
      HEAD(12, 7) +
      P('M12,18 L12,9') +
      P('M12,10 L16.5,12.5 M12,10 L8,13') +
      P('M12,18 L9.5,29.5 M12,18 L15.5,29.5') +
      P('M17,5.5 L22.5,4 M17.5,8 L23.5,8', 'ma-motion')),  // the sight lines

    recover: wrap(
      HEAD(16, 7) +
      P('M16,18.5 L16,9.5') +
      P('M16,11.5 L11,15.5 M16,11.5 L21,15.5') +     // arms low and open, nothing held
      P('M16,18.5 L12.5,29.5 M16,18.5 L19.5,29.5') +
      P('M11,3.4 Q16,0.6 21,3.4 M12.8,5.4 Q16,3.6 19.2,5.4', 'ma-motion')),

    feint: wrap(
      HEAD(18, 7.5) +
      P('M12,19 L17,10') +
      P('M17,10.5 L27.5,9', 'ma-motion') +           // the strike that is not one: dashed
      P('M17,10.5 L13.5,13.5') +
      P('M12,19 L8,29.5 M12,19 L15.5,24.5 L16,29.5')),

    dirty: wrap(
      P('M5,26.5 L13,19.5') +                        // the forearm
      P('M14,18.5 C16.5,15.5 18.5,13 20.5,10.5') +   // five fingers, spread and raking
      P('M14.5,19 C18,17 21,15 23.5,13') +
      P('M15,19.5 C19,18.5 22.5,17.5 25.5,16') +
      P('M15,20.5 C18.5,20.5 22,20.5 25,20') +
      P('M14.5,21.5 C17.5,22.5 20,23 22.5,23') +
      P('M27,8.5 L29.5,12 M28.5,15 L30.5,18', 'ma-motion')),

    headbutt: wrap(
      HEAD(21, 9, 2.6) +
      P('M13,20 L19.5,10.5') +                       // everything behind the forehead
      P('M19,11.5 L26.5,10 M18.5,13 L25.5,14.5') +   // collar ties, pulling in
      P('M13,20 L8.5,29.5 M13,20 L17,25 L17.5,29.5') +
      P('M26.5,5 L29.5,3.8 M27.5,7.5 L30.5,7', 'ma-motion'))
  };

  // ---- which archetype is this card ------------------------------------------------------------
  //
  // ID BEFORE TRAIT, TRAIT BEFORE GUESS. The id regexes catch the moves whose trait is true but
  // coarser than the picture ("Footwork" on a stomp; "Kick" on a mawashi that should whip, not
  // snap). The trait list then decides by PRIORITY: the concrete technique wins over the tactical
  // role, because the user-facing claim is "how the move looks when used" and a cross thrown as a
  // counter still looks like a cross.
  const ID_RULES = [
    [/headbutt/, 'headbutt'],
    [/stomp/, 'kick_axe'],
    [/sand|rake|gouge/, 'dirty'],
    [/weave|bob_|_bob/, 'evade'],
    // Dirty-trait cards whose ACTION is a clean technique aimed somewhere it should not be: the
    // Dirty tag already rides the face as a badge, so the glyph shows the motion.
    [/knee_to/, 'knee'], [/rising_low_blow/, 'punch_upper'], [/rabbit_punch/, 'punch_hook'],
    [/glove_grab/, 'trap'],
    // Grapple-trait cards that end with the other body in the air.
    [/shoulder_wheel|arm_spin/, 'throw'],
    // Footwork/Combination-trait cards whose picture is the strike the footwork delivers.
    [/linear_blitz/, 'punch_straight'], [/eight_limb/, 'elbow'],
    // ---- Season 1 props, classified by hand because the trait list cannot reach them ----
    //
    // `Prop`, `Thrown` and `ImprovisedWeapon` are in TRAIT_PRIORITY nowhere, so both of these would
    // fall through to the 'read' default — a poised figure standing still, which is the one outcome
    // coverage() exists to make impossible. Named here rather than by adding the traits to the
    // priority list, because a `Prop` trait says what is in the hand and not what the body does,
    // and the body is what move art draws.
    //
    // Beach Ball -> `dirty`, following Thrown Sand: the only other card in the game whose attack is
    // an object leaving the hand, and the pose is the same improvised hurl. NOT `throw`, which in
    // this registry means a grapple throw — it would draw a hip toss for a spiked beach ball.
    // Metal Detector -> `sweep`: the card attacks Low with a shaft swung along the floor, which is
    // what the sweep pose already is.
    [/beach_ball/, 'dirty'], [/metal_detector/, 'sweep'],
    // Season 1's player cards. Thirteen of the fifteen classify correctly from their traits alone;
    // these two do not, and both would have drawn something that contradicts the card rather than
    // merely being vague — which is the bar for adding a rule here.
    //
    // Uki Otoshi is a Judo drop throw. Its `Grapple` trait lands it on `clinch`, a pose of two
    // fighters tied up chest to chest, which is the one thing this technique never does: it drops
    // beneath a corner and puts them on the floor.
    // Sprawl and Rise is an uppercut on the way up out of a sprawl. `Punch` gives it
    // `punch_straight`, a jab down a flat line — the opposite shape to the one the card is named
    // for, and the rising line IS the technique.
    [/uki_otoshi/, 'throw'], [/sprawl_and_rise/, 'punch_upper']
  ];
  const KICK_BY_ID = [
    [/round|mawashi|hook_kick|spinning|turning|tornado|crescent|wheel|question/, 'kick_round'],
    [/axe|downward/, 'kick_axe'],
    [/side|yoko|thrust|driving|piston|blast_kick|ushiro/, 'kick_side']
  ];
  const PUNCH_BY_ID = [
    [/hook|shovel|overhand|haymaker|dempsey|roll/, 'punch_hook'],
    [/upper|rising/, 'punch_upper']
  ];
  const TRAIT_PRIORITY = [
    ['Knee', 'knee'], ['Elbow', 'elbow'], ['Sweep', 'sweep'], ['Trip', 'trip'],
    ['Throw', 'throw'], ['Sacrifice', 'throw'], ['Lift', 'throw'], ['Takedown', 'takedown'],
    ['Kick', 'KICK'], ['Punch', 'PUNCH'], ['HandStrike', 'punch_straight'], ['Strike', 'punch_straight'],
    ['Clinch', 'clinch'], ['Grip', 'clinch'], ['Control', 'clinch'], ['Grapple', 'clinch'],
    ['Trap', 'trap'], ['HandFighting', 'trap'], ['Sensitivity', 'trap'], ['Redirect', 'trap'],
    ['Guard', 'guard'], ['Structure', 'frame'], ['Frame', 'frame'],
    ['Counter', 'counter'], ['Feint', 'feint'],
    ['Footwork', 'footwork'], ['Angle', 'footwork'],
    ['Read', 'read'], ['Pressure', 'pressure'], ['Rotation', 'rotation'],
    ['Conditioning', 'recover'], ['Recovery', 'recover'],
    ['Dirty', 'dirty'],
    ['Centerline', 'punch_straight'], ['Power', 'punch_straight'],
    ['Combination', 'punch_straight'], ['Finisher', 'punch_straight']
  ];

  function refine(kind, id) {
    const table = kind === 'KICK' ? KICK_BY_ID : PUNCH_BY_ID;
    for (const [re, g] of table) if (re.test(id)) return g;
    return kind === 'KICK' ? 'kick_front' : 'punch_straight';
  }

  function keyFor(card) {
    if (!card) return null;
    const id = card.id || '';
    for (const [re, g] of ID_RULES) if (re.test(id)) return g;
    const traits = card.traits || [];
    for (const [trait, g] of TRAIT_PRIORITY) {
      if (traits.includes(trait)) return g === 'KICK' || g === 'PUNCH' ? refine(g, id) : g;
    }
    // Nothing matched: a poised figure rather than a wrong technique. Coverage() names these so
    // the fallback is a fact the prototype reports, not a hole it hides.
    return 'read';
  }

  // Which half of the game a pose belongs to, for surfaces that colour the motion arc by role —
  // the Technique Signature ideation's accent arc, mapped onto the app's existing role palette.
  const DEF_GLYPHS = ['guard', 'evade', 'recover', 'read', 'counter', 'frame', 'trap', 'footwork'];
  function roleOf(key) { return DEF_GLYPHS.includes(key) ? 'def' : 'atk'; }

  // ---- body / motion, split out of one drawing ---------------------------------------------------
  //
  // Every element in a glyph is a single self-closing <path> or <circle> generated by P()/HEAD()
  // above — no nesting, no text — which is what makes splitting them by tag safe here and would not
  // make it safe on markup this file did not write. `ma-motion` and `ma-contact` are the two motion
  // layers; everything else is body. Split once at load, not per render.
  const TAG = /<(?:path|circle)\b[^>]*\/>/g;
  const isMotion = tag => /class="[^"]*\bma-(?:motion|contact)\b/.test(tag);
  function splitGlyph(svg) {
    const inner = svg.replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '');
    const tags = inner.match(TAG) || [];
    const motion = tags.filter(isMotion);
    return {
      body: wrap(tags.filter(t => !isMotion(t)).join('')),
      motion: wrap(motion.join('')),
      count: tags.length,
      motionCount: motion.length
    };
  }
  const PARTS = {};
  Object.keys(GLYPHS).forEach(k => { PARTS[k] = splitGlyph(GLYPHS[k]); });

  // ---- where a fighter's figure for a pose lives -------------------------------------------------
  //
  // A fighter's own figure, then the shared one (the two poses with no fighter in them), then null.
  // Null is a supported answer, not a failure: it means the SVG pose renders, so a fighter with no
  // art yet — or a pose nobody has drawn — degrades to a drawing rather than to a broken image.
  // NEVER build this path by concatenation; see the generator's header for what that costs.
  function figurePath(artKey, pose) {
    const reg = window.MOVE_ART_FIGURES;
    if (!reg || !pose) return null;
    const own = artKey && reg[artKey] && reg[artKey][pose];
    if (own) return own;
    return (reg._shared && reg._shared[pose]) || null;
  }

  // ---- whose body performs the move ----------------------------------------------------------------
  //
  // The card is a move ITS OWNER throws, so the actor is the card's own fighter — not the opponent
  // the Attack half's silhouette is drawn on. (Those are different questions: the silhouette answers
  // "where does this land", which is a body it lands ON; this answers "who is doing it".)
  //
  // THREE WAYS A FIGHTER NAMES ITS ART, in falling order of directness:
  //   fighter.artKey     the deck builder, which is choosing art and has no deck yet
  //   DECK_META.artKey   a custom deck, whose art the store records when it merges the deck in
  //   fighter.deckId     the fifteen shipped decks, whose ids ARE art keys
  //
  // That last one is the coincidence js/fighter-art.js exists to stop relying on, so it is GUARDED:
  // the id is used only when the registry actually has a key by that name, which makes it a lookup
  // that happens to succeed rather than an assumption that happens to hold. A custom deck id
  // (`custom_ab12cd34ef`) is never in the registry, which is why the store writes artKey explicitly
  // — without that line every custom deck would silently fall back to the SVG poses.
  function artKeyFor(fighter) {
    if (!fighter) return null;
    if (fighter.artKey) return fighter.artKey;
    const meta = fighter.deckId && window.DECK_META && window.DECK_META[fighter.deckId];
    if (meta && meta.artKey) return meta.artKey;
    const reg = window.FIGHTER_ART;
    if (fighter.deckId && reg && reg[fighter.deckId]) return fighter.deckId;
    return null;
  }

  // opts: { cls, artKey, label }
  //
  // `label` is for the alt text and is deliberately the CARD's name rather than the pose's: a
  // screen reader hearing "punch_straight" learns an internal bucket, and the card is named on the
  // tile two lines above anyway — so the image is decorative and says so, and the pose travels in
  // data-move for styling and for tests.
  function glyphHtml(card, opts) {
    opts = (typeof opts === 'string') ? { cls: opts } : (opts || {});
    const key = keyFor(card);
    if (!key) return '';
    const src = figurePath(opts.artKey, key);
    const parts = PARTS[key];
    const inner = src
      ? `<img class="ma-figure" src="${src}" alt="" loading="lazy" decoding="async" draggable="false">${parts.motion}`
      : GLYPHS[key];
    // MEASURED PLACEMENT, and only for a raster. The three numbers come from the ink itself (see
    // MOVE_ART_GEOMETRY): they normalise apparent size across poses that fill their canvases very
    // differently, and they anchor on the ink's right and bottom edges so a roundhouse keeps its
    // foot. The SVG poses need none of it — they are drawn to a 32x32 viewBox that is already
    // normalised, which is exactly why the fallback has no vars rather than neutral ones.
    const geo = src && window.MOVE_ART_GEOMETRY && window.MOVE_ART_GEOMETRY[key];
    const style = geo ? ` style="--ma-fit:${geo.fit};--ma-rx:${geo.rx};--ma-by:${geo.by}"` : '';
    return `<span class="cc-moveart ${opts.cls || ''} ma-${roleOf(key)}${src ? ' has-figure' : ''}" ` +
           `data-move="${key}"${style}>${inner}</span>`;
  }

  // Every card -> its archetype, plus who fell through to the fallback, for the audit sheet.
  function coverage() {
    const out = { byCard: {}, byGlyph: {}, fallback: [] };
    Object.keys(window.CARDS || {}).forEach(id => {
      const card = window.CARDS[id];
      const key = keyFor(card);
      out.byCard[id] = key;
      (out.byGlyph[key] = out.byGlyph[key] || []).push(id);
      const traits = card.traits || [];
      const matched = ID_RULES.some(([re]) => re.test(id)) ||
        TRAIT_PRIORITY.some(([t]) => traits.includes(t));
      if (!matched) out.fallback.push(id);
    });
    return out;
  }

  // THE 26 ARCHETYPE KEYS ARE A FILENAME FORMAT. Each one names a directory entry under
  // assets/move-art/<fighter>/ for all fifteen fighters, so renaming one orphans fifteen files at
  // once and the tile silently falls back to the SVG pose — a downgrade with no error anywhere.
  // Pinned by name here and asserted in tests/move-art-test.js, the same guard the art keys in
  // js/fighter-art.js carry for the same reason. Adding an archetype is fine; renaming is not.
  //
  // `evade` is deliberately absent: it is a drawn pose that no card currently reaches, so no figure
  // was commissioned for it. The test asserts REACHABLE archetypes have art, which is the invariant
  // that matters — if a future card classifies to evade, that test fails and names it.
  const PINNED_ARCHETYPES = [
    'punch_straight', 'punch_hook', 'punch_upper',
    'kick_front', 'kick_round', 'kick_side', 'kick_axe',
    'sweep', 'knee', 'elbow', 'headbutt',
    'throw', 'takedown', 'trip', 'clinch',
    'guard', 'frame', 'trap', 'counter', 'footwork',
    'pressure', 'rotation', 'read', 'recover', 'feint', 'dirty'
  ];

  window.MoveArt = {
    GLYPHS, PARTS, PINNED_ARCHETYPES,
    keyFor, glyphHtml, coverage, roleOf, figurePath, splitGlyph, artKeyFor
  };
})();
